import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Divider,
} from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { LoadingButton } from "@mui/lab";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";
import { toastError, toastSuccess } from "src/utils/utils";

const textFieldStyles = {
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "#d0d7de",
    },
    "&:hover fieldset": {
      borderColor: "#7338AC",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#7338AC",
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#7338AC",
  },
};

const validationSchema = yup.object().shape({
  title: yup.string().required("Title is required"),
  description: yup.array().of(yup.string()).min(1, "At least one description item is required"),
});

const DialogAddModule = (props) => {
  const { open, onClose, onSuccess, mode = "add", dataToEdit, courseId } = props;
  const [loading, setLoading] = useState(false);
  const [descriptionItems, setDescriptionItems] = useState([""]);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      title: "",
      description: [""],
    },
    resolver: yupResolver(validationSchema),
    mode: "onChange",
  });

  const watchedDescription = watch("description");

  useEffect(() => {
    if (open && dataToEdit && mode === "edit") {
      const descriptionArray = Array.isArray(dataToEdit.description)
        ? dataToEdit.description
        : [dataToEdit.description || ""];

      setDescriptionItems(descriptionArray);
      reset({
        title: dataToEdit.title || "",
        description: descriptionArray,
      });
    } else if (open && mode === "add") {
      setDescriptionItems([""]);
      reset({
        title: "",
        description: [""],
      });
    }
  }, [dataToEdit, open, mode, reset]);

  const addDescriptionItem = () => {
    const newItems = [...descriptionItems, ""];
    setDescriptionItems(newItems);
    setValue("description", newItems);
  };

  const removeDescriptionItem = (index) => {
    if (descriptionItems.length > 1) {
      const newItems = descriptionItems.filter((_, i) => i !== index);
      setDescriptionItems(newItems);
      setValue("description", newItems);
    }
  };

  const updateDescriptionItem = (index, value) => {
    const newItems = [...descriptionItems];
    newItems[index] = value;
    setDescriptionItems(newItems);
    setValue("description", newItems);
  };

  const onSubmit = (data) => {
    setLoading(true);

    const payload = {
      course_id: courseId,
      title: data.title,
      description: data.description.filter(item => item.trim() !== ""), // Remove empty items
    };

    if (mode === "add") {
      axiosInstance
        .post(ApiEndPoints.MODULES.create, payload)
        .then((response) => {
          toastSuccess(response.data?.message || "Module added successfully");
          onSuccess();
          onClose();
        })
        .catch((error) => {
          toastError(error);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      // Edit mode - include course_id and is_active for updates
      const editPayload = {
        title: data.title,
        description: data.description.filter(item => item.trim() !== ""),
      };

      axiosInstance
        .patch(ApiEndPoints.MODULES.update(dataToEdit.id), editPayload)
        .then((response) => {
          toastSuccess(response.data?.message || "Module updated successfully");
          onSuccess();
          onClose();
        })
        .catch((error) => {
          toastError(error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "8px",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {mode === "add" ? "Add Module" : "Edit Module"}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            color: "grey.500",
            "&:hover": {
              backgroundColor: "grey.100",
            },
          }}
        >
          <HighlightOffIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        <Box component="form" sx={{ mt: 1 }}>
          <Controller
            name="title"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Title"
                error={!!errors.title}
                helperText={errors.title?.message}
                sx={textFieldStyles}
                margin="normal"
              />
            )}
          />

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Description
            </Typography>
            {descriptionItems.map((item, index) => (
              <Box key={index} sx={{ display: "flex", gap: 1, mb: 1, alignItems: "flex-start" }}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  value={item}
                  onChange={(e) => updateDescriptionItem(index, e.target.value)}
                  placeholder={`Description item ${index + 1}`}
                  inputProps={{ maxLength: 70 }}
                  sx={textFieldStyles}
                />
                {descriptionItems.length > 1 && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => removeDescriptionItem(index)}
                    sx={{ minWidth: "auto", px: 1 }}
                  >
                    Remove
                  </Button>
                )}
              </Box>
            ))}
            <Button
              variant="outlined"
              onClick={addDescriptionItem}
              sx={{ mt: 1 }}
            >
              Add Description Item
            </Button>
            {errors.description && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                {errors.description.message}
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 3, gap: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderColor: "#d0d7de",
            color: "#656d76",
            "&:hover": {
              borderColor: "#7338AC",
              color: "#7338AC",
            },
          }}
        >
          Cancel
        </Button>
        <LoadingButton
          onClick={handleSubmit(onSubmit)}
          loading={loading}
          variant="contained"

        >
          {mode === "add" ? "Add Module" : "Update Module"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default DialogAddModule;
